import React, { useState } from 'react';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

const TDS_RENT_RATES = {
  individual: { // Section 194IB
    threshold: 600000, // Per year (₹50,000/month)
    rate: 5,
    section: '194IB',
    description: 'TDS by Individual/HUF on rent paid',
  },
  business: { // Section 194I
    threshold: 240000, // Per year
    rateLand: 10,
    ratePlant: 2,
    section: '194I',
    description: 'TDS by business entities on rent paid',
  }
};

const TDSRentCalculator = () => {
  const [payer, setPayer] = useState('individual');
  const [monthlyRent, setMonthlyRent] = useState(55000);
  const [rentType, setRentType] = useState('land'); // land or plant
  const [panAvailable, setPanAvailable] = useState(true);
  const [months, setMonths] = useState(12);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const mr = Number(monthlyRent) || 0;
    const m = Number(months) || 0;
    const yearlyRent = mr * m;
    let threshold, rate, section, description;
    
    if (payer === 'individual') {
      threshold = TDS_RENT_RATES.individual.threshold;
      rate = TDS_RENT_RATES.individual.rate;
      section = TDS_RENT_RATES.individual.section;
      description = TDS_RENT_RATES.individual.description;
    } else {
      threshold = TDS_RENT_RATES.business.threshold;
      rate = rentType === 'land' ? TDS_RENT_RATES.business.rateLand : TDS_RENT_RATES.business.ratePlant;
      section = TDS_RENT_RATES.business.section;
      description = TDS_RENT_RATES.business.description;
    }
    
    // No PAN = 20% TDS
    if (!panAvailable) {
      rate = 20;
    }
    
    let isTDSApplicable = false;
    let tdsAmount = 0;
    let reason = '';
    
    if (yearlyRent >= threshold) {
      isTDSApplicable = true;
      
      if (payer === 'individual') {
        // For 194IB, TDS is deducted only from last month's rent
        const lastMonthRent = mr;
        const yearlyTDS = (yearlyRent * rate) / 100;
        // But capped at last month's rent
        tdsAmount = Math.min(yearlyTDS, lastMonthRent);
        reason = `Yearly rent (₹${yearlyRent.toLocaleString()}) exceeds ₹50,000/month threshold`;
      } else {
        // For 194I, TDS on each payment
        tdsAmount = (yearlyRent * rate) / 100;
        reason = `Yearly rent exceeds ₹2,40,000 threshold`;
      }
    } else {
      reason = `Yearly rent below threshold of ₹${threshold.toLocaleString()}. No TDS applicable.`;
    }
    
    const netRentReceived = yearlyRent - tdsAmount;
    const monthlyTDS = payer === 'business' ? (mr * rate) / 100 : 0;

    setResult({
      section,
      description,
      monthlyRent,
      yearlyRent,
      threshold,
      rate,
      tdsAmount,
      netRentReceived,
      isTDSApplicable,
      reason,
      monthlyTDS,
      panAvailable,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('TDS on Rent Calculation', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Description', 'Value']],
      body: [
        ['Section', result.section],
        ['Payer Type', payer === 'individual' ? 'Individual/HUF' : 'Business Entity'],
        ['Monthly Rent', formatCurrencyForPDF(result.monthlyRent)],
        ['Period', `${months} months`],
        ['Yearly Rent', formatCurrencyForPDF(result.yearlyRent)],
        ['Threshold Limit', formatCurrencyForPDF(result.threshold)],
        ['TDS Rate', `${result.rate}%`],
        ['PAN Available', result.panAvailable ? 'Yes' : 'No (20% rate applied)'],
        ['TDS Applicable', result.isTDSApplicable ? 'Yes' : 'No'],
        ['TDS Amount', formatCurrencyForPDF(result.tdsAmount)],
        ['Net Rent Receivable', formatCurrencyForPDF(result.netRentReceived)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('TDS_Rent_Calculation.pdf');
  };

  return (
    <div className="calculator-page">
      <SEO 
        title="TDS on Rent Calculator"
        description="Free TDS on Rent Calculator India - Calculate TDS deduction on rent payment above ₹50,000/month."
        keywords="TDS on rent calculator, rent TDS calculator, Section 194IB calculator, TDS on rent payment, landlord TDS calculator"
        canonicalPath="/tds-rent"
      />
      <div className="calculator-header">
        <h1>🏠 TDS on Rent Calculator</h1>
        <p>Calculate TDS on rent under Section 194I and 194IB</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Who is Paying Rent?</label>
            <div className="tabs">
              <button 
                className={`tab ${payer === 'individual' ? 'active' : ''}`}
                onClick={() => setPayer('individual')}
              >
                Individual/HUF
              </button>
              <button 
                className={`tab ${payer === 'business' ? 'active' : ''}`}
                onClick={() => setPayer('business')}
              >
                Business/Company
              </button>
            </div>
            <small style={{ color: '#64748b', fontSize: '12px', marginTop: '8px', display: 'block' }}>
              {payer === 'individual' 
                ? 'Section 194IB - Applicable when rent > ₹50,000/month'
                : 'Section 194I - Applicable when rent > ₹2,40,000/year'}
            </small>
          </div>

          {payer === 'business' && (
            <div className="input-group">
              <label>Type of Rent</label>
              <select value={rentType} onChange={(e) => setRentType(e.target.value)}>
                <option value="land">Land / Building / Furniture (10%)</option>
                <option value="plant">Plant / Machinery / Equipment (2%)</option>
              </select>
            </div>
          )}

          <div className="grid-2-cols">
            <div className="input-group">
              <label>Monthly Rent (₹)</label>
              <input
                type="number"
                value={monthlyRent === '' ? '' : monthlyRent}
                onChange={(e) => setMonthlyRent(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Number of Months</label>
              <input
                type="number"
                value={months === '' ? '' : months}
                onChange={(e) => setMonths(e.target.value === '' ? '' : Number(e.target.value))}
                max="12"
              />
            </div>
          </div>

          <div className="input-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={panAvailable}
                onChange={(e) => setPanAvailable(e.target.checked)}
                style={{ width: 'auto' }}
              />
              Landlord's PAN Available (20% TDS if not available)
            </label>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate TDS
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ 
                background: result.isTDSApplicable 
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                  : 'linear-gradient(135deg, #10b981, #059669)'
              }}>
                <h3>TDS on Rent</h3>
                <div className="amount">{formatCurrency(result.tdsAmount)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  {result.isTDSApplicable ? `Section ${result.section} @ ${result.rate}%` : 'No TDS Applicable'}
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Section</span>
                  <span className="value">{result.section}</span>
                </div>
                <div className="result-item">
                  <span className="label">Monthly Rent</span>
                  <span className="value">{formatCurrency(result.monthlyRent)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Yearly Rent ({months} months)</span>
                  <span className="value">{formatCurrency(result.yearlyRent)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Threshold</span>
                  <span className="value">{formatCurrency(result.threshold)}/year</span>
                </div>
                <div className="result-item">
                  <span className="label">TDS Rate</span>
                  <span className="value">{result.rate}%</span>
                </div>
                {!result.panAvailable && (
                  <div className="result-item" style={{ background: '#fef3c7' }}>
                    <span className="label">⚠️ No PAN</span>
                    <span className="value" style={{ color: '#f59e0b' }}>Higher Rate (20%) Applied</span>
                  </div>
                )}
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">Total TDS</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.tdsAmount)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Net Rent Receivable</span>
                  <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.netRentReceived)}</span>
                </div>
              </div>

              <div className="info-box" style={{ marginTop: '16px' }}>
                <p><strong>Note:</strong> {result.reason}</p>
              </div>

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter rent details to calculate TDS</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 TDS on Rent Rules</h4>
        <table className="schedule-table" style={{ marginTop: '12px' }}>
          <thead>
            <tr>
              <th>Section</th>
              <th>Payer</th>
              <th>Threshold</th>
              <th>Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>194IB</td>
              <td>Individual/HUF</td>
              <td>₹50,000/month</td>
              <td>5%</td>
            </tr>
            <tr>
              <td>194I</td>
              <td>Business</td>
              <td>₹2,40,000/year</td>
              <td>10% (Land) / 2% (Plant)</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TDSRentCalculator;
